/**
 * Client-side referral service
 * Consolidates all referral-related logic in one place
 */

import { useEffect } from 'react';

const REFERRER_STORAGE_KEY = 'gayborhood_referrer';

/**
 * Get the stored referrer code from localStorage
 */
export function getReferrerCode(): string | null {
  return localStorage.getItem(REFERRER_STORAGE_KEY);
}

/**
 * Store a referrer code in localStorage
 * Normalizes the code to uppercase
 */
export function setReferrerCode(code: string): void {
  localStorage.setItem(REFERRER_STORAGE_KEY, code.toUpperCase().trim());
}

/**
 * Clear the referrer code from localStorage
 * Should be called after a successful booking/payment
 */
export function clearReferrerCode(): void {
  localStorage.removeItem(REFERRER_STORAGE_KEY);
}

/**
 * Check if there's a stored referrer code
 */
export function hasReferrerCode(): boolean {
  return !!getReferrerCode();
}

/**
 * Hook to capture referral code from URL on mount
 * Automatically stores the code and cleans up the URL
 */
export function useReferralCapture(): void {
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');
    
    if (refCode) {
      setReferrerCode(refCode);
      
      // Clean up the URL by removing the ref parameter
      urlParams.delete('ref');
      const newUrl = urlParams.toString() 
        ? `${window.location.pathname}?${urlParams.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);
}

/**
 * Generate a shareable referral link for a given referral code
 */
export function generateReferralLink(referralId: string): string {
  return `https://outclose.com/?ref=${referralId}`;
}

// ============================================================================
// "+1" invite codes — same lifecycle pattern as referrals: captured at page
// load, stored in localStorage, redeemed once the user is signed in.
// ============================================================================

const INVITE_STORAGE_KEY = "outclose_invite_code";

export function getInviteCode(): string | null {
  return localStorage.getItem(INVITE_STORAGE_KEY);
}

export function setInviteCode(code: string): void {
  localStorage.setItem(INVITE_STORAGE_KEY, code.toUpperCase().trim());
}

export function clearInviteCode(): void {
  localStorage.removeItem(INVITE_STORAGE_KEY);
}

